/**
 * useOrganizationVideos Hook
 * Manages organization-wide video state with centralized fetching
 * Provides videos to child routes via VideosContext
 */

import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import * as prospectMediaService from '../services/prospect-media';
import {
  EMPTY_VIDEO_FILTERS,
  type VideoFilters,
  type VideoWithProspect,
} from '../types/prospect-media';
import { useAuth } from './useAuth';
import { useDebounce } from './useDebounce';

/** Typing pause before a search re-queries. Long enough that a word isn't 5 requests. */
const SEARCH_DEBOUNCE_MS = 300;

/**
 * The API's ceiling (`pageSize > 100` is rejected and silently falls back to 15). This
 * screen renders one flat grid with no pager, so it asks for as much as the server will
 * give in one go; `totalCount` is what tells the user when there is more than that.
 */
const PAGE_SIZE = 100;

export function useOrganizationVideos() {
  const { effectiveOrganizationId } = useAuth();
  // One piece of state, not two: removing a video has to drop it from `items` and
  // decrement `totalCount` together. As separate useStates that meant a setTotalCount
  // nested inside the setVideos updater, which StrictMode double-invokes in development —
  // one delete, two decrements.
  const [list, setList] = useState<{ items: VideoWithProspect[]; totalCount: number }>({
    items: [],
    totalCount: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filters, setFilters] = useState<VideoFilters>(EMPTY_VIDEO_FILTERS);

  // Only the search box needs debouncing — the pickers change one click at a time, and
  // waiting 300ms after a click reads as lag rather than as batching.
  const debouncedSearchTerm = useDebounce(filters.searchTerm, SEARCH_DEBOUNCE_MS);

  // Responses can land out of order once filters change mid-flight, and the API gives no
  // way to tell which request a response belongs to. Only the newest request may write.
  const latestRequest = useRef(0);

  const {
    tagIds,
    teamId,
    uploadedBy,
    uploadedAfter,
    uploadedBefore,
  } = filters;

  // Depend on the joined ids, not the array: a new array each render would refetch forever.
  const tagIdsKey = tagIds.join(',');

  const fetchVideos = useCallback(async () => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;

    try {
      setLoading(true);
      setError(null);
      const response = await prospectMediaService.getAllVideos({
        pageSize: PAGE_SIZE,
        searchTerm: debouncedSearchTerm,
        tagIds: tagIdsKey ? tagIdsKey.split(',') : [],
        teamId,
        uploadedBy,
        uploadedAfter,
        uploadedBefore,
      });
      if (latestRequest.current !== requestId) return;
      setList({ items: response.items, totalCount: response.totalCount });
    } catch (err) {
      if (latestRequest.current !== requestId) return;
      const errorMessage = err instanceof Error ? err.message : 'Failed to load videos';
      console.error('[OrganizationVideos] Failed to fetch videos', err);
      setError(errorMessage);
    } finally {
      if (latestRequest.current === requestId) setLoading(false);
    }
  }, [
    effectiveOrganizationId,
    debouncedSearchTerm,
    tagIdsKey,
    teamId,
    uploadedBy,
    uploadedAfter,
    uploadedBefore,
  ]);

  useEffect(() => {
    fetchVideos();
  }, [fetchVideos]);

  const clearFilters = useCallback(() => setFilters(EMPTY_VIDEO_FILTERS), []);

  // Drops a deleted video from the list in place. A refetch would work too, but it costs a
  // round trip and re-requests a signed thumbnail URL for every remaining tile just to
  // remove one. `totalCount` moves with it, or the filter bar keeps advertising a match
  // that is no longer there.
  const removeVideo = useCallback((videoId: string) => {
    setList(current => {
      const items = current.items.filter(v => v.id !== videoId);
      // Returning `current` unchanged for an id we never held keeps the count honest if a
      // delete is somehow reported twice.
      if (items.length === current.items.length) return current;
      return { items, totalCount: Math.max(0, current.totalCount - 1) };
    });
  }, []);

  // Memoized: this is the value of VideosContext, and filter state now lives here. An
  // unstable object would re-render every consumer under the dashboard's <Outlet /> on
  // each keystroke in the video search box.
  return useMemo(
    () => ({
      videos: list.items,
      /** Matches across the whole library, which can exceed the page rendered. */
      totalCount: list.totalCount,
      loading,
      error,
      refresh: fetchVideos,
      removeVideo,
      filters,
      setFilters,
      clearFilters,
    }),
    [list, loading, error, fetchVideos, removeVideo, filters, clearFilters]
  );
}
